import { createSelector } from '@reduxjs/toolkit';
import type { RootStateP } from '../store';

/**
 * Selector base para Config_General
 */
const selectConfigGeneral = (state: RootStateP) => state.configuration.Data?.Config_General;

/**
 * Selector memoizado para crear un mapa indexado de configuración
 * Esto convierte O(n) búsquedas en O(1) accesos directos
 */
export const selectConfigMap = createSelector(
  [selectConfigGeneral],
  (configGeneral) => {
    if (!configGeneral) return {};
    
    return configGeneral.reduce((map, item) => {
      map[item.Cgn_Nombre] = item.Cgn_Resultado;
      return map;
    }, {} as Record<string, string>);
  }
);

/**
 * Selector para obtener un valor de configuración específico
 * @param configName - Nombre de la configuración (ej: 'InvalidationTime')
 */
export const createConfigSelector = (configName: string) =>
  createSelector(
    [selectConfigMap],
    (configMap) => configMap[configName]
  );

/**
 * Selectores específicos de configuración para el módulo de resultados
 */
export const selectInvalidationTime = createSelector(
  [selectConfigMap],
  (configMap) => parseInt(configMap['InvalidationTime'] ?? '0')
);

export const selectMaxCountRegs = createSelector(
  [selectConfigMap],
  (configMap) => parseInt(configMap['MaxCountRegs'] ?? '1000')
);
  
export const selectInvalidateOnlyOwnAnalitos = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['InvalidateOnlyOwrAnalitos'] ?? 'false')
);

export const selectModuleBarcode = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleBarcode'] ?? 'false')
);

export const selectModuleAlarms = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleAlarms'] ?? 'false')
);

export const selectModuleEmail = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleEmail'] ?? 'false')
);

export const selectModuleWhatsApp = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleWhatsApp'] ?? 'false')
);

export const selectModuleTracking = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleTracking'] ?? 'false')
);

export const selectModuleClinicalData = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleClinicalData'] ?? 'false')
);

export const selectModuleSamples = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleSamples'] ?? 'false')
);

export const selectModuleAttachment = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleAttachment'] ?? 'false')
);

export const selectModuleInformedConsents = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleInformedConsents'] ?? 'false')
);

export const selectModuleRemisions = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleRemisions'] ?? 'false')
);

export const selectModuleBilling = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleBilling'] ?? 'false')
);

export const selectColumnsContainerOrderOrders = createSelector(
  [selectConfigMap],
  (configMap) => configMap['ColumnsContainerOrderOrders']
);

export const selectColumnsContainerPatientOrders = createSelector(
  [selectConfigMap],
  (configMap) => configMap['ColumnsContainerPatientOrders']
);

export const selectStateStudyNewOrder = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['InitialStateTestForNewOrder'] ?? 'false')
);

export const selectModuleLocation = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleLocation'] ?? 'false')
);

export const selectExternalRemissionsFont = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ExternalRemissionsFont'] ?? 'false')
);

export const selectExternalRemissionsEquivalField = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ExternalRemissionsEquivalField'] ?? 'false')
);

/**
 * Selector para todas las configuraciones de módulos en un solo objeto
 */
export const selectAllModulesConfig = createSelector(
  [selectConfigMap],
  (configMap) => ({
    barcode: JSON.parse(configMap['ModuleBarcode'] ?? 'false'),
    alarms: JSON.parse(configMap['ModuleAlarms'] ?? 'false'),
    email: JSON.parse(configMap['ModuleEmail'] ?? 'false'),
    whatsApp: JSON.parse(configMap['ModuleWhatsApp'] ?? 'false'),
    tracking: JSON.parse(configMap['ModuleTracking'] ?? 'false'),
    clinicalData: JSON.parse(configMap['ModuleClinicalData'] ?? 'false'),
    samples: JSON.parse(configMap['ModuleSamples'] ?? 'false'),
    attachment: JSON.parse(configMap['ModuleAttachment'] ?? 'false'),
    informedConsents: JSON.parse(configMap['ModuleInformedConsents'] ?? 'false'),
    remisions: JSON.parse(configMap['ModuleRemisions'] ?? 'false'),
    billing: JSON.parse(configMap['ModuleBilling'] ?? 'false'),
  })
);
